import React, { useEffect, useRef } from 'react';
import { useAppConfig } from '../../context/AppConfigContext';
import { FunnelImage } from './FunnelImage';
import { useModalScrollLock } from './useModalScrollLock';

export interface ResumeModalProps {
  summary: {
    nombre: string;
    raza?: string;
    peso?: string;
    /** Stable breed key (snake_case) used to resolve the portrait image. */
    breedKey?: string;
  };
  daysAgo: number;
  onContinue: () => void;
  onStartFresh: () => void;
}

export const ResumeModal: React.FC<ResumeModalProps> = ({ summary, daysAgo, onContinue, onStartFresh }) => {
  const { t } = useAppConfig();
  useModalScrollLock();

  // a11y de modal bloqueante (auditoría loop frontend N1): foco inicial al CTA,
  // focus trap (Tab no escapa al fondo) y Escape. Escape mapea a onContinue (la
  // acción primaria, NO destructiva: conserva el plan guardado) en vez de
  // descartar los datos del cliente.
  const dialogRef = useRef<HTMLDivElement>(null);
  const continueBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    continueBtnRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onContinue();
        return;
      }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusables = dialog.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onContinue]);

  const title = t('funnel.resume.title', 'Tienes un plan guardado');

  const whenLabel =
    daysAgo === 0 ? t('funnel.resume.today', 'hoy') : t('funnel.resume.days_ago', 'hace {n} días', { n: daysAgo });

  const summaryParts: string[] = [];
  if (summary.raza) summaryParts.push(summary.raza);
  if (summary.peso) summaryParts.push(summary.peso);
  summaryParts.push(t('funnel.resume.edited_when', 'editado {when}', { when: whenLabel }));
  const summaryLine = summaryParts.join(' · ');

  const body = t(
    'funnel.resume.body',
    'Puedes continuar con los datos de {nombre} o empezar un plan nuevo desde cero.',
    { nombre: summary.nombre },
  );

  const continueLabel = t('funnel.resume.continue', 'Continuar con {nombre}', { nombre: summary.nombre });
  const startFreshLabel = t('funnel.resume.start_fresh', 'Empezar nuevo plan');

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="resume-modal-title"
        className="bg-white rounded-[20px] p-6 max-w-sm w-full shadow-xl space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Breed portrait when we know it; falls back to a paw emoji
            otherwise. The portrait makes the modal feel personal —
            "Continuar con Juan" is much warmer next to a Border
            Terrier face than next to a generic icon. */}
        <div className="flex justify-center">
          {summary.breedKey ? (
            <div className="w-20 h-20 rounded-2xl overflow-hidden bg-[#FAF7F1] relative">
              <FunnelImage
                src={`/images/breeds/${summary.breedKey}.png`}
                alt={summary.raza || summary.nombre}
                className="absolute inset-0 w-full h-full object-contain p-1"
                fallback={
                  <span
                    className="absolute inset-0 flex items-center justify-center text-3xl"
                    role="img"
                    aria-label={summary.raza || summary.nombre}
                  >
                    🐾
                  </span>
                }
              />
            </div>
          ) : (
            <span className="text-3xl" role="img" aria-label={summary.nombre}>
              🐾
            </span>
          )}
        </div>

        <h3 id="resume-modal-title" className="font-fredoka text-lg font-semibold text-black text-center">
          {title}
        </h3>

        <p className="font-poppins text-sm text-black/70 text-center leading-snug">{summaryLine}</p>
        <p className="font-poppins text-sm text-black/55 text-center leading-snug">{body}</p>

        <div className="flex flex-col gap-2 pt-1">
          <button
            ref={continueBtnRef}
            type="button"
            onClick={onContinue}
            className="w-full py-3 bg-primary text-white font-fredoka font-semibold rounded-[14px] hover:opacity-90 transition-opacity"
          >
            {continueLabel}
          </button>
          <button
            type="button"
            onClick={onStartFresh}
            className="w-full py-3 bg-gray-100 text-black/70 font-fredoka font-medium rounded-[14px] hover:bg-gray-200 transition-colors"
          >
            {startFreshLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
